// The audio-visual canvas. It is drawn at social-video resolution and
// reacts to three things: the sound itself (analyser), the notes you play
// (ripples, color) and the effect settings (trails, glow, particles).

export type Aspect = '9:16' | '1:1' | '16:9';
export type VisStyle = 'halo' | 'skyline' | 'ribbon';
export type Palette = 'notes' | 'ink';
export type Backdrop = 'paper' | 'night';
export type ImageFit = 'fill' | 'fit';

export interface VisSettings {
  aspect: Aspect;
  style: VisStyle;
  palette: Palette;
  backdrop: Backdrop;
  caption: string;
  tag: boolean;
  imageFit: ImageFit;
}

/** Effect mixes 0..1, plus saturation drive. */
export interface FxSnapshot {
  reverb: number;
  saturation: number;
  drive: number;
  granular: number;
}

export const SIZES: Record<Aspect, [number, number]> = {
  '9:16': [1080, 1920],
  '1:1': [1080, 1080],
  '16:9': [1920, 1080],
};

/** One color per pitch class, going round the color wheel from C. */
export const NOTE_COLORS = [
  '#e0533d', '#e8783a', '#e9a23b', '#d4b93a', '#9dbf45', '#4fae6a',
  '#2fa59a', '#3b93c9', '#4c6fd6', '#7a5cd6', '#b054c4', '#d44e8e',
];

const THEMES: Record<Backdrop, { bg: [number, number, number]; ink: [number, number, number] }> = {
  paper: { bg: [243, 239, 230], ink: [35, 32, 28] },
  night: { bg: [20, 19, 18], ink: [237, 231, 218] },
};

type RGB = [number, number, number];
const hex = (h: string): RGB => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
const mix = (a: RGB, b: RGB, t: number): RGB => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
const rgba = (c: RGB, a = 1) => `rgba(${c[0] | 0},${c[1] | 0},${c[2] | 0},${a})`;

interface Ripple {
  pc: number;
  born: number;
  vel: number;
  color: RGB;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  age: number;
  size: number;
  color: RGB;
}

export class Visualizer {
  private g: CanvasRenderingContext2D;
  private time: Float32Array<ArrayBuffer>;
  private freq: Uint8Array<ArrayBuffer>;
  private raf = 0;
  private running = false;
  private last = 0;
  private level = 0;
  private bass = 0;
  /** Per pitch class: how strongly it's sounding, 0..1, with a soft release. */
  private energy = new Float32Array(12);
  private active = new Map<number, number>(); // note -> velocity
  private ripples: Ripple[] = [];
  private particles: Particle[] = [];
  private image: ImageBitmap | null = null;
  private font = '"Shantell Sans Variable", "Shantell Sans", ui-rounded, system-ui, sans-serif';

  constructor(
    private canvas: HTMLCanvasElement,
    private analyser: AnalyserNode,
    private fx: () => FxSnapshot,
    public settings: VisSettings,
  ) {
    this.g = canvas.getContext('2d', { alpha: false })!;
    this.time = new Float32Array(analyser.fftSize);
    this.freq = new Uint8Array(analyser.frequencyBinCount);
    this.applySize();
  }

  update(s: Partial<VisSettings>): void {
    const aspectChanged = s.aspect && s.aspect !== this.settings.aspect;
    this.settings = { ...this.settings, ...s };
    if (aspectChanged) this.applySize();
    if (!this.running) this.frame(performance.now());
  }

  setImage(img: ImageBitmap | null): void {
    this.image?.close();
    this.image = img;
    if (!this.running) this.frame(performance.now());
  }

  noteOn(note: number, velocity: number): void {
    this.active.set(note, velocity / 127);
    const pc = note % 12;
    this.ripples.push({ pc, born: performance.now(), vel: velocity / 127, color: this.noteColor(pc) });
    if (this.ripples.length > 40) this.ripples.shift();
  }

  noteOff(note: number): void {
    this.active.delete(note);
  }

  clearNotes(): void {
    this.active.clear();
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    const loop = (t: number) => {
      if (!this.running) return;
      this.frame(t);
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  private applySize(): void {
    const [w, h] = SIZES[this.settings.aspect];
    this.canvas.width = w;
    this.canvas.height = h;
    this.canvas.dataset.aspect = this.settings.aspect;
    this.particles = [];
  }

  private noteColor(pc: number): RGB {
    return this.settings.palette === 'notes' ? hex(NOTE_COLORS[pc]) : [...THEMES[this.settings.backdrop].ink];
  }

  private frame(now: number): void {
    const dt = Math.min(0.05, (now - this.last) / 1000);
    this.last = now;
    const { g, canvas } = this;
    const W = canvas.width;
    const H = canvas.height;
    const u = Math.min(W, H) / 1080;
    const s = this.settings;
    const theme = THEMES[s.backdrop];
    const fx = this.fx();
    const sat = fx.saturation * (0.35 + 0.65 * fx.drive);

    this.analyser.getFloatTimeDomainData(this.time);
    this.analyser.getByteFrequencyData(this.freq);
    let sum = 0;
    for (let i = 0; i < this.time.length; i++) sum += this.time[i] * this.time[i];
    const rms = Math.sqrt(sum / this.time.length);
    this.level += (Math.min(1, rms * 4) - this.level) * 0.25;
    let b = 0;
    for (let i = 1; i < 9; i++) b += this.freq[i];
    this.bass += (b / (8 * 255) - this.bass) * 0.2;

    // Notes light up fast and fade slowly (longer with more reverb).
    const held = new Float32Array(12);
    for (const [note, vel] of this.active) held[note % 12] = Math.max(held[note % 12], 0.55 + vel * 0.45);
    const up = 1 - Math.pow(0.0001, dt);
    const down = 1 - Math.exp(-dt / (0.35 + fx.reverb * 1.2));
    let lead = -1;
    for (let pc = 0; pc < 12; pc++) {
      const e = this.energy[pc];
      this.energy[pc] = e + (held[pc] - e) * (held[pc] > e ? up : down);
      if (this.energy[pc] > 0.03 && (lead < 0 || this.energy[pc] > this.energy[lead])) lead = pc;
    }
    const inkRGB = theme.ink;
    const tint = (pc: number): RGB => mix(inkRGB, hex(NOTE_COLORS[pc]), Math.min(1, this.energy[pc] * 2.2));
    const col: RGB = s.palette === 'notes' && lead >= 0 ? tint(lead) : [...inkRGB];
    const lit = s.palette === 'notes' ? [...this.energy.keys()].filter((pc) => this.energy[pc] > 0.03) : [];

    // Reverb leaves trails: the more wet, the less each frame is erased.
    const persistence = fx.reverb * 0.88;
    g.globalCompositeOperation = 'source-over';
    g.shadowBlur = 0;
    if (this.image && s.imageFit === 'fill') {
      g.globalAlpha = 1 - persistence * 0.9;
      this.drawCover(this.image, W, H, 1.02 + this.bass * 0.06);
      g.globalAlpha = (1 - persistence * 0.9) * 0.28;
      g.fillStyle = rgba(theme.bg);
      g.fillRect(0, 0, W, H);
      g.globalAlpha = 1;
    } else {
      g.fillStyle = rgba(theme.bg, 1 - persistence);
      g.fillRect(0, 0, W, H);
      if (this.image) this.drawLogo(this.image, W, H, u);
    }

    g.lineCap = 'round';
    g.lineJoin = 'round';
    if (sat > 0.04) {
      g.shadowColor = rgba(col, 0.8);
      g.shadowBlur = sat * 36 * u;
    }
    const lineWidth = (3.5 + sat * 9) * u;
    // Saturation squares off the drawn wave, just like it does to the sound.
    const drive = 1 + sat * 7;
    const shape = (v: number) => Math.tanh(v * drive) / Math.tanh(drive);
    const points = this.drawStyle(W, H, u, col, lineWidth, shape, lit.map(tint));
    g.shadowBlur = 0;

    this.drawRipples(now, W, H, u, fx.reverb);
    this.updateParticles(dt, points, fx.granular, u, col);
    this.drawText(W, H, u, theme.ink);
  }

  /** Draws the chosen style; returns sample points particles can spawn from. */
  private drawStyle(
    W: number,
    H: number,
    u: number,
    col: RGB,
    lw: number,
    shape: (v: number) => number,
    chord: RGB[],
  ): [number, number][] {
    const g = this.g;
    // A chord paints the shape with every one of its colors, not a muddy average.
    const paint = (make: () => CanvasGradient): string | CanvasGradient => {
      if (chord.length < 2) return rgba(col);
      const grad = make();
      const n = chord.length;
      chord.forEach((c, i) => grad.addColorStop(i / n, rgba(c)));
      grad.addColorStop(1, rgba(chord[0]));
      return grad;
    };
    const t = this.time;
    const pts: [number, number][] = [];
    const style = this.settings.style;

    if (style === 'halo') {
      const cx = W / 2;
      const cy = H / 2;
      const R = Math.min(W, H) * 0.27 * (1 + this.bass * 0.18);
      const N = 360;
      const step = Math.floor(t.length / N);
      g.strokeStyle = paint(() => g.createConicGradient(-Math.PI / 2, cx, cy));
      g.lineWidth = lw;
      g.beginPath();
      for (let i = 0; i <= N; i++) {
        const v = shape(t[(i % N) * step] * 2.2);
        // Blend the seam so the ring closes smoothly.
        const edge = i > N - 20 ? (i - (N - 20)) / 20 : 0;
        const vv = v * (1 - edge) + shape(t[0] * 2.2) * edge;
        const a = (i / N) * Math.PI * 2 - Math.PI / 2;
        const r = R + vv * R * 0.45;
        const x = cx + Math.cos(a) * r;
        const y = cy + Math.sin(a) * r;
        if (i === 0) g.moveTo(x, y);
        else g.lineTo(x, y);
        if (i % 6 === 0) pts.push([x, y]);
      }
      g.stroke();
      // A quieter inner ring traces the spectrum.
      g.lineWidth = lw * 0.45;
      g.strokeStyle = rgba(col, 0.4);
      g.beginPath();
      const bins = 180;
      for (let i = 0; i <= bins; i++) {
        const bin = Math.floor(Math.pow((i % bins) / bins, 2) * 400) + 2;
        const v = this.freq[bin] / 255;
        const a = (i / bins) * Math.PI * 2 - Math.PI / 2;
        const r = R * 0.78 - v * R * 0.22;
        const x = cx + Math.cos(a) * r;
        const y = cy + Math.sin(a) * r;
        if (i === 0) g.moveTo(x, y);
        else g.lineTo(x, y);
      }
      g.stroke();
    } else if (style === 'skyline') {
      const bars = 48;
      const base = H * (W > H ? 0.68 : 0.62);
      const gap = W * 0.12;
      const bw = (W - gap * 2) / bars;
      const maxH = H * (W > H ? 0.42 : 0.3);
      g.fillStyle = paint(() => g.createLinearGradient(gap, 0, W - gap, 0));
      for (let i = 0; i < bars; i++) {
        // Log-spaced bins from ~50 Hz to ~8 kHz, where instruments live.
        const f0 = 50 * Math.pow(160, i / bars);
        const f1 = 50 * Math.pow(160, (i + 1) / bars);
        const hz = this.analyser.context.sampleRate / this.analyser.fftSize;
        let v = 0;
        let c = 0;
        for (let bin = Math.floor(f0 / hz); bin <= Math.ceil(f1 / hz); bin++) {
          v = Math.max(v, this.freq[bin] ?? 0);
          c++;
        }
        const val = shape(Math.pow(v / 255, 1.6) * 1.2);
        const h = Math.max(4 * u, val * maxH);
        const x = gap + i * bw + bw * 0.18;
        const w = bw * 0.64;
        roundRect(g, x, base - h, w, h, w / 2);
        g.globalAlpha = 0.18;
        roundRect(g, x, base + 10 * u, w, h * 0.45, w / 2);
        g.globalAlpha = 1;
        if (c) pts.push([x + w / 2, base - h]);
      }
    } else {
      // Ribbon: a triggered oscilloscope, so the wave stands still.
      let start = 0;
      for (let i = 1; i < t.length / 2; i++) {
        if (t[i - 1] < 0 && t[i] >= 0) {
          start = i;
          break;
        }
      }
      const span = Math.floor(t.length / 2);
      const amp = H * (W > H ? 0.28 : 0.16);
      const cy = H / 2;
      const layers: [number, number][] = [[-6 * u, 0.25], [6 * u, 0.25], [0, 1]];
      const main = paint(() => g.createLinearGradient(W * 0.06, 0, W * 0.94, 0));
      for (const [off, alpha] of layers) {
        g.strokeStyle = alpha === 1 ? main : rgba(col, alpha);
        g.lineWidth = lw;
        g.beginPath();
        for (let i = 0; i <= 300; i++) {
          const v = shape((t[start + Math.floor((i / 300) * span)] ?? 0) * 2.5);
          const x = W * 0.06 + (i / 300) * W * 0.88;
          const y = cy + v * amp + off;
          if (i === 0) g.moveTo(x, y);
          else g.lineTo(x, y);
          if (alpha === 1 && i % 6 === 0) pts.push([x, y]);
        }
        g.stroke();
      }
    }
    return pts;
  }

  private drawRipples(now: number, W: number, H: number, u: number, reverb: number): void {
    const g = this.g;
    const life = 1100 + reverb * 1600;
    this.ripples = this.ripples.filter((r) => now - r.born < life);
    for (const r of this.ripples) {
      const p = (now - r.born) / life;
      const ease = 1 - Math.pow(1 - p, 3);
      let x = W / 2;
      let y = H / 2;
      let r0 = Math.min(W, H) * 0.27;
      if (this.settings.style !== 'halo') {
        x = W * 0.12 + ((r.pc + 0.5) / 12) * W * 0.76;
        y = this.settings.style === 'skyline' ? H * (W > H ? 0.68 : 0.62) : H / 2;
        r0 = 20 * u;
      }
      const grow = this.settings.style === 'halo' ? 0.1 + r.vel * 0.12 : 0.08 + r.vel * 0.12;
      const rad = r0 + ease * Math.min(W, H) * grow;
      g.strokeStyle = rgba(r.color, (1 - p) * (0.35 + r.vel * 0.5));
      g.lineWidth = (2 + r.vel * 5) * u * (1 - p * 0.6);
      g.beginPath();
      g.arc(x, y, rad, 0, Math.PI * 2);
      g.stroke();
    }
  }

  private updateParticles(dt: number, pts: [number, number][], granular: number, u: number, col: RGB): void {
    const g = this.g;
    // Granular mix sprays particles off the shape, more when it's loud.
    const rate = granular * (this.level * 900 + 6);
    let spawn = rate * dt;
    while (spawn > 0 && pts.length && this.particles.length < 700) {
      if (spawn < 1 && Math.random() > spawn) break;
      spawn--;
      const [x, y] = pts[(Math.random() * pts.length) | 0];
      const a = Math.random() * Math.PI * 2;
      const sp = (40 + Math.random() * 220) * u;
      this.particles.push({
        x,
        y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        life: 0.5 + Math.random() * 1.3,
        age: 0,
        size: (2 + Math.random() * 5) * u,
        color: [...col],
      });
    }
    this.particles = this.particles.filter((p) => (p.age += dt) < p.life);
    for (const p of this.particles) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= 0.985;
      p.vy *= 0.985;
      g.fillStyle = rgba(p.color, (1 - p.age / p.life) * 0.85);
      g.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
    }
  }

  private drawText(W: number, H: number, u: number, inkColor: RGB): void {
    const g = this.g;
    const s = this.settings;
    g.textAlign = 'center';
    g.textBaseline = 'alphabetic';
    if (s.caption.trim()) {
      g.fillStyle = rgba(inkColor, 0.92);
      g.font = `600 ${Math.round(48 * u)}px ${this.font}`;
      g.fillText(s.caption.trim(), W / 2, H - (s.tag ? 130 : 90) * u);
    }
    if (s.tag) {
      g.fillStyle = rgba(inkColor, 0.55);
      g.font = `500 ${Math.round(26 * u)}px ${this.font}`;
      g.fillText('made with spiel', W / 2, H - 60 * u);
    }
  }

  private drawCover(img: ImageBitmap, W: number, H: number, zoom: number): void {
    const scale = Math.max(W / img.width, H / img.height) * zoom;
    const w = img.width * scale;
    const h = img.height * scale;
    this.g.drawImage(img, (W - w) / 2, (H - h) / 2, w, h);
  }

  private drawLogo(img: ImageBitmap, W: number, H: number, u: number): void {
    const box = Math.min(W, H) * (this.settings.style === 'halo' ? 0.3 : 0.26) * (1 + this.bass * 0.08);
    const scale = Math.min(box / img.width, box / img.height);
    const w = img.width * scale;
    const h = img.height * scale;
    const cy = this.settings.style === 'halo' ? H / 2 : this.settings.style === 'skyline' ? H * 0.3 : H * 0.24;
    this.g.drawImage(img, (W - w) / 2, cy - h / 2 + (this.settings.style === 'halo' ? 0 : 10 * u), w, h);
  }
}

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  g.beginPath();
  g.roundRect(x, y, w, h, Math.min(r, h / 2));
  g.fill();
}
